import { Box, Typography, Stack, Card } from '@mui/material';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import { useOTSPQuery } from '../../api/oneTimeScheduledPaymentInfo';
import PaymentAmountRadio from '../../components/PaymentAmountRadio';
import StatusAlert from '../../components/styledComponents/StatusAlert';
import colors from '../../constants/colors';
import {
  PaymentAmountType,
  PaymentAmountTypeLabels,
  StatusType,
} from '../../constants/enums';
import useAnalytics from '../../hooks/useAnalytics';
import { usePaymentsContext } from '../../store/payments';
import { addSingleSelectedPayment } from '../../store/paymentsReducer';
import PayAnotherAmountField from './components/PayAnotherAmountField';
import SharedButtons from './components/SharedButtons';
import PaymentSubHeader from './components/SubHeader';
import { AmountFormValues } from './validation';

const PaymentPage = () => {
  const {
    state: { selectedBill, singleSelectedPayment },
    dispatch,
  } = usePaymentsContext();
  const location = useLocation();
  const { data } = useWelcomeQuery();
  const { data: otspData } = useOTSPQuery(data?.policies);
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const scheduledPayment =
    otspData?.[selectedBill.policyNumber]?.schedulePaymentDetails; // OTSP for the current policy

  const [tempSelectedValue, setTempSelectedValue] = useState<PaymentAmountType>(
    singleSelectedPayment?.paymentType ?? PaymentAmountType.TOTAL_DUE
  );
  const [tempAmount, setTempAmount] = useState(
    singleSelectedPayment?.amount ?? selectedBill.minimumDueAmount
  );

  const handleClick = (value: PaymentAmountType) => {
    setTempSelectedValue(value);
  };

  const handleSubmit = (values: AmountFormValues) => {
    track('Payment Managed', {
      location: 'Feature Card',
      event_type: 'User Information Entered',
      payment_type: 'One Policy',
      payment_amount: values.amount,
      payment_selection: PaymentAmountTypeLabels[tempSelectedValue],
      policy_number: selectedBill.policyNumber,
      state: selectedBill.riskState,
      policy_type: selectedBill.policyType,
    });
    dispatch(
      addSingleSelectedPayment({
        ...selectedBill,
        amount: values.amount,
        paymentType: tempSelectedValue,
        policyType: selectedBill.policyType,
      })
    );
    navigate('/payment/method', { state: location.state });
  };

  return (
    <Box
      data-testid="paymentPage"
      sx={{
        display: 'flex',
        flex: 1,
        width: 1,
        flexDirection: 'column',
        minHeight: '100%',
        backgroundColor: colors.csaaGray,
        px: { mobile: 2, tablet: 3, desktop: 19.5 },
        pb: 3,
      }}
    >
      {!selectedBill?.minimumDueAmount && <Navigate to="/bills" replace />}
      <PaymentSubHeader headerText="How much would you like to pay?" />
      <Card
        component={Stack}
        spacing={{ mobile: 2, tablet: 3, desktop: 2 }}
        sx={{
          borderRadius: 2.5,
          mt: 3,
          px: {
            mobile: 2,
            tablet: 3,
            desktop: 5,
          },
          py: {
            mobile: 2,
            tablet: 3,
            desktop: 5,
          },
          height: 'auto',
          boxShadow: '0px 4px 8px 0px rgba(25, 30, 43, 0.08)',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 23, tablet: 28 },
            lineHeight: { mobile: '32px', tablet: '56px', desktop: '40px' },
          }}
        >
          Payment amount
        </Typography>
        <StatusAlert
          open={
            !!(scheduledPayment?.paymentAmount && scheduledPayment?.paymentDate)
          }
          text={`There is already a one-time payment of ${scheduledPayment?.paymentAmount} scheduled for ${scheduledPayment?.paymentDate}.`}
          status={StatusType.WARNING}
        />
        <Stack
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            handleSubmit({ amount: tempAmount });
          }}
          direction="column"
          spacing={{ mobile: 2, tablet: 3 }}
          sx={{
            mt: { mobile: 2, tablet: 3 },
          }}
        >
          <PaymentAmountRadio
            selectedValue={tempSelectedValue}
            handleClick={(value) => {
              handleClick(value);
              setTempAmount(selectedBill.minimumDueAmount);
            }}
            value={PaymentAmountType.TOTAL_DUE}
            title={PaymentAmountTypeLabels.TOTAL_DUE}
            amount={selectedBill.minimumDueAmount}
            testId="pay-total-due-radio"
          />
          <PaymentAmountRadio
            selectedValue={tempSelectedValue}
            handleClick={(value) => {
              handleClick(value);
              setTempAmount(selectedBill.totalDueAmount);
            }}
            value={PaymentAmountType.FULL}
            title={PaymentAmountTypeLabels.FULL}
            amount={selectedBill.totalDueAmount}
            testId="pay-in-full-radio"
          />
          <PaymentAmountRadio
            selectedValue={tempSelectedValue}
            data-testid="anotherAmount"
            handleClick={(value) => {
              handleClick(value);
              setTempAmount('');
            }}
            value={PaymentAmountType.ANOTHER_AMOUNT}
            title={PaymentAmountTypeLabels.ANOTHER_AMOUNT}
            testId="pay-another-amount-radio"
          />
          {tempSelectedValue === PaymentAmountType.ANOTHER_AMOUNT && (
            <PayAnotherAmountField
              autoSubmit
              bill={selectedBill}
              onSubmit={(value) => {
                setTempAmount(value.amount);
              }}
              handleChange={setTempAmount}
              tempAmount={tempAmount}
              testId="pay-another-amount-field"
            />
          )}
          <SharedButtons disabled={Number(tempAmount) <= 0} />
        </Stack>
      </Card>
    </Box>
  );
};

export default PaymentPage;
